import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/siteInfo';
import { testimonials } from '@/mocks/testimonials';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteInfo.name}`,
  description: siteInfo.description,
  url: 'https://naptevq.cn/about',
  inLanguage: 'zh-CN',
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    email: siteInfo.contactEmail,
    url: 'https://naptevq.cn/',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naptevq.cn/' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://naptevq.cn/about' },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '《艳母的荒唐赌约》是什么类型的小说？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '《艳母的荒唐赌约》是一部都市情感小说，讲述了豪门深处一段荒诞不经却又引人深思的故事。小说以独特的视角展现了人性在欲望与道德之间的挣扎，情节跌宕起伏，人物刻画入微。',
      },
    },
    {
      '@type': 'Question',
      name: '小说目前更新到多少章了？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `截至最新更新，《艳母的荒唐赌约》已更新${siteInfo.totalChapters}章，总字数约${siteInfo.totalWords}。目前状态为${siteInfo.status}，持续更新中。`,
      },
    },
    {
      '@type': 'Question',
      name: '如何开始阅读？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以从首页点击"开始阅读"按钮，直接从第一章开始阅读。也可以浏览五大故事篇章，选择感兴趣的章节开始。',
      },
    },
    {
      '@type': 'Question',
      name: '可以在手机上阅读吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '当然可以！我们的网站完全适配手机端，无论您使用手机、平板还是电脑，都能获得流畅舒适的阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系作者或团队？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `您可以通过邮件 ${siteInfo.contactEmail} 与我们取得联系，我们会在收到邮件后尽快回复。`,
      },
    },
  ],
};

const faqData = [
  {
    q: '《艳母的荒唐赌约》是什么类型的小说？',
    a: '这是一部都市情感小说，讲述了豪门深处一段荒诞不经却又引人深思的故事。小说以独特的视角展现了人性在欲望与道德之间的挣扎，情节跌宕起伏，人物刻画入微，深受广大读者喜爱。',
  },
  {
    q: '小说目前更新到多少章了？',
    a: `截至最新更新，已更新${siteInfo.totalChapters}章，总字数约${siteInfo.totalWords}。目前状态为${siteInfo.status}，我们会持续为您带来精彩内容。`,
  },
  {
    q: '如何开始阅读？',
    a: '您可以从首页点击"开始阅读"按钮，直接从第一章开始阅读。也可以浏览五大故事篇章，选择感兴趣的章节开始。所有章节均免费提供。',
  },
  {
    q: '可以在手机上阅读吗？',
    a: '当然可以！我们的网站完全适配手机端，无论您使用手机、平板还是电脑，都能获得流畅舒适的阅读体验。文章页面经过精心设计，提供最佳阅读排版。',
  },
  {
    q: '如何联系作者或团队？',
    a: `您可以通过邮件 ${siteInfo.contactEmail} 与我们取得联系。无论是内容建议、合作洽谈还是其他问题，我们都会在收到邮件后尽快回复。`,
  },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEO
        title={`关于我们 - ${siteInfo.name} | 品牌介绍与联系方式`}
        description={`关于《${siteInfo.name}》，${siteInfo.description.slice(0, 100)}... 了解我们的品牌故事、读者评价与联系方式。`}
        keywords="艳母的荒唐赌约,关于我们,品牌介绍,联系方式,小说作者"
        canonicalUrl="https://naptevq.cn/about"
        schema={{ ...aboutSchema, ...breadcrumbSchema, ...faqSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100/70 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-900 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="py-16 md:py-20 bg-background-100/50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于我们
            </h1>
            <p className="text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              一个专注于优质文学内容的创作团队，致力于为读者带来触动人心的故事
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="flex flex-col gap-4 text-sm text-foreground-700 leading-relaxed">
              <p>
                《{siteInfo.name}》是我们倾力打造的一部都市情感小说。故事以豪门为背景，以一场荒诞的赌约为引子，展开了一段关于爱情、欲望、背叛与救赎的精彩故事。
              </p>
              <p>
                小说主角林婉儿是一个普通家庭的女孩，因命运的安排嫁入豪门，却发现自己从一开始就是一场精心布局的赌局中的棋子。在面对命运的捉弄时，她没有选择屈服，而是在磨难中逐渐成长，最终找到了属于自己的力量。
              </p>
              <p>
                我们的创作团队坚持每天更新，已累计创作超过{siteInfo.totalChapters}章，总字数超过{siteInfo.totalWords}。每一章都经过精心打磨，力求为读者带来最好的阅读体验。
              </p>
              <p>
                我们相信，好的故事能够跨越时间和空间，触动每一个读者的心灵。感谢每一位读者的支持和陪伴，是你们让我们有动力继续前行。
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 bg-background-100/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-fill text-xs ${i < t.rating ? 'text-amber-400' : 'text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="mt-3 text-xs text-foreground-400">{t.date}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {faqData.map((faq, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    {openFaq === idx ? (
                      <i className="ri-arrow-up-s-line text-foreground-500 flex-shrink-0"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line text-foreground-500 flex-shrink-0"></i>
                    )}
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-20 bg-background-100/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-8 max-w-lg mx-auto">
              无论是内容建议、合作洽谈还是其他问题，都欢迎与我们取得联系
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`mailto:${siteInfo.contactEmail}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                发送邮件
              </a>
              <span className="text-sm text-foreground-500">
                邮箱：{siteInfo.contactEmail}
              </span>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}